/**
 * Capture never fails: every note is saved in this browser (IndexedDB) before it is sent, and sent
 * with a client-chosen id so retries can't duplicate. Saved notes survive reloads and going offline.
 */
import { useSyncExternalStore } from "react";
import { newId, type CaptureContext, type CaptureDTO } from "../../contract";
import { ApiError, api } from "./api";
import { t } from "./i18n";

export interface OutboxFile { id: string; mime: string; name?: string; blob: Blob; duration_ms?: number }
export interface OutboxItem {
  id: string;
  owner: string;                  // ranch:member, so another account on this browser never sends it
  kind: CaptureDTO["kind"];
  text: string | null;
  context: CaptureContext;
  files: OutboxFile[];
  created_at: string;
  state: "saved" | "sending" | "failed";
  error?: string;
  attempts: number;
}

const DB = "hondo-v2";
const STORE = "outbox";
let memory: OutboxItem[] = [];
let items: OutboxItem[] = [];
const listeners = new Set<() => void>();
const emit = () => { items = [...items]; listeners.forEach((l) => l()); };
let owner = "";

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "id" });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  const db = await open();
  if (!db) return null;
  return new Promise((resolve) => {
    const r = fn(db.transaction(STORE, mode).objectStore(STORE));
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => resolve(null);
  });
}

async function persist(item: OutboxItem): Promise<boolean> {
  const ok = await tx("readwrite", (s) => s.put(item));
  if (ok === null) { memory = [...memory.filter((m) => m.id !== item.id), item]; return false; }
  return true;
}

async function remove(id: string): Promise<void> {
  await tx("readwrite", (s) => s.delete(id));
  memory = memory.filter((m) => m.id !== id);
}

async function load(): Promise<void> {
  const stored = (await tx<OutboxItem[]>("readonly", (s) => s.getAll() as IDBRequest<OutboxItem[]>)) ?? [];
  items = [...stored, ...memory].filter((i) => i.owner === owner).sort((a, b) => a.created_at.localeCompare(b.created_at));
  emit();
}

/** Called once a session is known. Scopes the outbox to this ranch and person. */
export async function startOutbox(ranchId: string, memberId: string): Promise<void> {
  owner = `${ranchId}:${memberId}`;
  await load();
  void flush();
}

export async function saveCapture(input: { kind: OutboxItem["kind"]; text?: string | null; context?: CaptureContext; files?: Omit<OutboxFile, "id">[] }): Promise<{ id: string; durable: boolean }> {
  const item: OutboxItem = {
    id: newId("cap"), owner, kind: input.kind, text: input.text?.trim() || null, context: input.context ?? {},
    files: (input.files ?? []).map((f) => ({ ...f, id: newId("f") })), created_at: new Date().toISOString(), state: "saved", attempts: 0,
  };
  const durable = await persist(item);
  items = [...items, item];
  emit();
  void flush();
  return { id: item.id, durable };
}

let flushing = false;
export async function flush(): Promise<void> {
  if (flushing || !owner) return;
  flushing = true;
  try {
    for (const item of items.filter((i) => i.state !== "sending")) {
      if (item.state === "failed" && item.attempts > 8) continue;
      item.state = "sending";
      emit();
      try {
        await api.post("/captures", {
          id: item.id, kind: item.kind, text: item.text, context: item.context, client_created_at: item.created_at,
          files: item.files.map((f) => ({ id: f.id, mime: f.mime, name: f.name ?? null, bytes: f.blob.size, duration_ms: f.duration_ms ?? null })),
        });
        for (const f of item.files) await api.put(`/captures/${item.id}/files/${f.id}`, f.blob, { "content-type": f.mime });
        await remove(item.id);
        items = items.filter((i) => i.id !== item.id);
        sent.add(item.id);
        emit();
      } catch (err) {
        item.attempts++;
        item.state = "failed";
        item.error = err instanceof ApiError && err.status ? err.message : t("Waiting for a connection");
        await persist(item);
        emit();
        // A 4xx other than auth will not fix itself; leave it visible for the person.
        if (!(err instanceof ApiError) || err.status === 0 || err.status >= 500 || err.status === 401) break;
      }
    }
  } finally {
    flushing = false;
  }
}

export async function discardSaved(id: string): Promise<void> {
  await remove(id);
  items = items.filter((i) => i.id !== id);
  emit();
}

/** Ids sent from this browser this session, so the composer can follow them. */
export const sent = new Set<string>();

export function useOutbox(): OutboxItem[] {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => items);
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => void flush());
  setInterval(() => { if (items.some((i) => i.state !== "sending")) void flush(); }, 30_000);
}
